import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
  IconButton,
  Alert,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Image as ImageIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  ContactPhone as ContactIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  RocketLaunch as RocketLaunchIcon
} from '@mui/icons-material';

/**
 * Venue Activation Checklist Component
 * Shows staff what requirements are complete/missing for venue activation
 */
const VenueActivationChecklist = ({ venueData, onRequirementClick, onActivateVenue, isActivating }) => {
  const [expanded, setExpanded] = useState(true);

  const handleToggle = () => {
    setExpanded(!expanded);
  };
  
  // Check if venue is already active
  const isVenueActive = venueData?.status === 'AC' || venueData?.status === 'PA';

  // Calculate checklist items with fieldId for scrolling to specific sections
  const checklistItems = [
    {
      id: 'name',
      label: 'Venue Name',
      icon: <DescriptionIcon />,
      completed: !!(venueData?.name && venueData.name.trim()),
      tab: 'general',
      fieldId: 'venue-section-basic'
    },
    {
      id: 'address',
      label: 'Address',
      icon: <LocationIcon />,
      completed: !!(venueData?.single_address && venueData.single_address.trim()),
      tab: 'general',
      fieldId: 'venue-section-location'
    },
    {
      id: 'short_description',
      label: 'Short Description',
      icon: <DescriptionIcon />,
      completed: !!(venueData?.description && venueData.description.trim()),
      tab: 'general',
      fieldId: 'venue-section-basic'
    },
    {
      id: 'full_description',
      label: 'Full Description',
      icon: <DescriptionIcon />,
      completed: !!(venueData?.full_description && venueData.full_description.trim()),
      tab: 'general',
      fieldId: 'venue-section-fulldesc'
    },
    {
      id: 'location',
      label: 'Map Location (not 0,0)',
      icon: <LocationIcon />,
      completed: venueData?.latitude && venueData?.longitude && 
                 (parseFloat(venueData.latitude) !== 0.0 || parseFloat(venueData.longitude) !== 0.0),
      tab: 'general',
      fieldId: 'venue-section-location'
    },
    {
      id: 'main_image',
      label: 'Main Venue Image',
      icon: <ImageIcon />,
      completed: !!(venueData?.image),
      tab: 'general',
      fieldId: 'venue-section-image'
    },
    {
      id: 'gallery',
      label: 'Gallery (min 5 images)',
      icon: <ImageIcon />,
      completed: (venueData?.gallery?.length || 0) >= 5,
      details: `${venueData?.gallery?.length || 0} / 5 images`,
      tab: 'gallery',
      fieldId: 'venue-section-gallery'
    },
    {
      id: 'contact',
      label: 'Contact (email or phone)',
      icon: <ContactIcon />,
      completed: !!(venueData?.contact_email || venueData?.contact_phone_1),
      tab: 'general',
      fieldId: 'venue-section-contact'
    },
    {
      id: 'opening_hours',
      label: 'Opening Hours',
      icon: <ScheduleIcon />,
      completed: venueData?.open_time && venueData.open_time.length > 0,
      details: `${venueData?.open_time?.length || 0} days configured`,
      tab: 'opening',
      fieldId: 'venue-section-opening'
    },
    {
      id: 'bookable_resource',
      label: 'At least 1 Hot Desk or Meeting Room',
      icon: <CategoryIcon />,
      completed: (() => {
        let hasActiveHotDesk = false;
        let hasActiveMeetingRoom = false;
        venueData?.floors?.forEach(floor => {
          floor.work_areas?.filter(wa => wa.status === 'AC').forEach(wa => {
            if (wa.hot_desks?.some(r => r.status === 'AC')) hasActiveHotDesk = true;
            if (wa.meeting_rooms?.some(r => r.status === 'AC')) hasActiveMeetingRoom = true;
          });
        });
        return hasActiveHotDesk || hasActiveMeetingRoom;
      })(),
      tab: 'catalogue',
      fieldId: 'venue-section-catalogue'
    }
  ];

  const completedCount = checklistItems.filter(item => item.completed).length;
  const totalCount = checklistItems.length;
  const progress = (completedCount / totalCount) * 100;
  const isComplete = completedCount === totalCount;
  
  // Only show incomplete items in the list
  const incompleteItems = checklistItems.filter(item => !item.completed);

  // Hide the checklist completely when venue is already active
  if (isVenueActive) {
    return null;
  }
  
  // Show activation banner when all requirements are met but venue is not active
  if (isComplete && !isVenueActive) {
    return (
      <Card
        sx={{
          mb: 3,
          border: '2px solid',
          borderColor: 'success.main',
          boxShadow: 2,
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" component="div" color="success.dark">
                Ready to Go Live! 🎉
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All requirements are complete. Your venue is ready to be activated.
              </Typography>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Once activated, your venue will appear in search results and users will be able to book spaces.
          </Alert>
          
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            startIcon={isActivating ? <CircularProgress size={20} color="inherit" /> : <RocketLaunchIcon />}
            onClick={onActivateVenue}
            disabled={isActivating || !onActivateVenue}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 5
              }
            }}
          >
            {isActivating ? 'Activating...' : 'Activate Venue Now'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const getTabLabel = (tabId) => {
    const labels = {
      general: 'General Info',
      gallery: 'Gallery',
      catalogue: 'Catalogue',
      opening: 'Opening Time'
    };
    return labels[tabId] || tabId;
  };

  return (
    <Card
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          sx={{ cursor: 'pointer' }}
          onClick={handleToggle}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" component="div">
              Venue Activation Checklist
            </Typography>
            <Chip
              icon={<WarningIcon />}
              label="Incomplete"
              color="warning"
              size="small"
            />
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary">
              {completedCount} of {totalCount} requirements complete
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main'
              }
            }}
          />
        </Box>

        <Collapse in={expanded}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Complete all requirements below to activate your venue and make it bookable by users.
          </Alert>

          <List dense>
            {incompleteItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  sx={{
                    cursor: onRequirementClick ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: onRequirementClick ? 'action.hover' : 'transparent'
                    }
                  }}
                  onClick={() => {
                    if (onRequirementClick) {
                      onRequirementClick(item.tab, item.fieldId);
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CancelIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {item.icon}
                        <Typography variant="body2">{item.label}</Typography>
                        {onRequirementClick && (
                          <Chip
                            label={`Go to ${getTabLabel(item.tab)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={item.details}
                  />
                </ListItem>
                {index < incompleteItems.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default VenueActivationChecklist;
